import type { CatalogueItemPricePreview } from "@shared-core/data/catalogue-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Badge } from "../../primitives/badge";
import { useVariantCostRefresh } from "./useVariantCostRefresh";
import { Loader2, RefreshCw } from "lucide-react";

// Internal/CSR-only cost-lineage trace shown next to the quoted price in the
// ordering configurator. CatalogueItemPage is reachable only by internal
// staff holding CATALOGUE_ORDER — no customer-facing page renders this, and
// none may gain access to PricePreviewResponseSchema's lineage fields.
//
// States (per docs/features/product-catalogue/12_variant_cost_refresh_frontend_notes.md,
// "Option 2 — subtle indicator, CSR-facing"):
//   - fresh manufacturing-cost quote: normal, minimal trace
//   - stale/failed with retained last-known-good cost: amber warning, offer refresh
//   - missing cost / quantity-break fallback: labelled as a manual price

const REFRESH_KEY = "cost-freshness-indicator";

export function CostFreshnessIndicator({
  itemId,
  quantity,
  preview,
  onRefreshed,
}: {
  itemId: string;
  /** The quantity the preview was resolved for — scopes a targeted refresh. */
  quantity: number;
  preview: CatalogueItemPricePreview | null;
  onRefreshed: () => void;
}) {
  const {
    refreshingKey,
    requestRefresh,
    pendingGuardrail,
    confirmGuardrail,
    cancelGuardrail,
  } = useVariantCostRefresh(itemId, onRefreshed);

  if (!preview) return null;

  // Scope the refresh to just the resolved (variant, quantity) pair when we
  // know it — a manufacturing-cost quote always has a resolved variant, so
  // this is the common case. Falling back to a whole-item refresh only
  // happens if the lineage is ever incomplete; label it plainly so the CSR
  // isn't surprised by the wider blast radius.
  const scoped = preview.variantId != null;
  const refresh = () =>
    requestRefresh(
      REFRESH_KEY,
      scoped ? { variantId: preview.variantId!, quantity } : {},
    );
  const refreshLabel = scoped ? "Refresh" : "Refresh all costs";
  const refreshing = refreshingKey === REFRESH_KEY;

  const guardrailDialog = (
    <AlertDialog
      open={pendingGuardrail !== null}
      onOpenChange={(open) => {
        if (!open) cancelGuardrail();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refresh a large number of costs?</AlertDialogTitle>
          <AlertDialogDescription>
            {pendingGuardrail?.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmGuardrail}>
            Refresh anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (preview.baseSource === "quantity_break") {
    return (
      <p className="text-xs text-muted-foreground">
        Manual quantity-break price — no manufacturing cost cached for this
        configuration.
      </p>
    );
  }

  if (preview.costStatus === "stale" || preview.costStatus === "failed") {
    return (
      <>
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <span>
            <Badge variant="warning" className="mr-1.5">
              {preview.costStatus === "failed"
                ? "Cost refresh failed"
                : "Cost stale"}
            </Badge>
            {preview.costStaleReason ? `${preview.costStaleReason}. ` : ""}
            Using last known cost from{" "}
            {formatDateOrDash(preview.costRefreshedAt)}
            {preview.tharsternEstimateRef &&
              ` (${preview.tharsternEstimateRef})`}
            .
          </span>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex shrink-0 items-center gap-1 font-medium text-amber-900 hover:underline disabled:opacity-50 dark:text-amber-200"
          >
            {refreshing ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            {refreshLabel}
          </button>
        </div>
        {guardrailDialog}
      </>
    );
  }

  if (preview.costStatus === "pending") {
    return (
      <p className="text-xs text-muted-foreground">
        Manufacturing cost refresh pending for this configuration.
      </p>
    );
  }

  // Priced between (or a little beyond) this variant's own cached quantities
  // — a real Tharstern-measured rate applied to a quantity that has no cache
  // row of its own, conservative by construction (docs/features/
  // product-catalogue/61_quantity_interpolation_design.md). Informational,
  // not a warning: unlike stale/failed, this is the mechanism working as
  // designed, not a cost needing attention. No refresh action — there is no
  // single (variant, quantity) row at a non-cached quantity to refresh.
  if (preview.costStatus === "interpolated") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-2.5 py-1.5 text-xs text-sky-900 dark:border-sky-900/50 dark:bg-sky-900/20 dark:text-sky-200">
        <Badge variant="outline" className="mr-1.5">
          Priced between cached quantities
        </Badge>
        <span>
          {preview.costStaleReason ? `${preview.costStaleReason}. ` : ""}
          {preview.tharsternEstimateRef &&
            `Anchor estimate ${preview.tharsternEstimateRef}. `}
          Confirmed exactly when this quantity is ordered.
        </span>
      </div>
    );
  }

  // Fresh manufacturing-cost quote — normal; a minimal trace for CSRs who
  // want to see the lineage without it competing with the price itself.
  return (
    <p className="text-xs text-muted-foreground">
      Cost fresh
      {preview.tharsternEstimateRef && ` · ${preview.tharsternEstimateRef}`}
      {preview.weight != null && ` · ${preview.weight}kg`}
    </p>
  );
}
